<script setup lang="ts">
  import { storeToRefs } from 'pinia';
  import { watch } from 'vue';
  import { useRoute } from 'vue-router';

  import { useVersionCheck } from '@/composables/useVersionCheck';
  import { useLocaleStore } from '@/stores/locale.store';

  /**
   * Mounts the new-deployment detector once at app level. Shows a brief
   * bilingual snackbar when a new build is live, then auto-refreshes the
   * stale tab at the next safe moment - a route change or a tab refocus -
   * never over unsaved work.
   */

  // Whether the user is mid-edit right now - a conservative, form-agnostic
  // guard so a refocus auto-reload never eats text someone is typing.
  function isEditingNow(): boolean {
    const el = document.activeElement as HTMLElement | null;
    if (!el) return false;
    const tag = el.tagName;
    return (
      tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable === true
    );
  }

  const { isLTR } = storeToRefs(useLocaleStore());

  const { updateAvailable, reloadNow, reloadIfSafe } = useVersionCheck({
    isUnsafe: isEditingNow,
  });

  const route = useRoute();

  // A route change is an inherently safe moment: the user navigated away from
  // wherever they were, so reloading to the new build loses nothing.
  watch(
    () => route.path,
    () => {
      if (updateAvailable.value) reloadIfSafe();
    }
  );
</script>

<template>
  <v-snackbar
    v-if="updateAvailable"
    :model-value="updateAvailable"
    persistent
    :timeout="-1"
    style="z-index: 999999999"
  >
    {{
      isLTR
        ? 'New version — refreshing…'
        : 'نسخة جديدة — يتم التحديث…'
    }}
    <template #actions>
      <v-btn variant="text" @click="reloadNow">
        {{ isLTR ? 'Refresh now' : 'تحديث الآن' }}
      </v-btn>
    </template>
  </v-snackbar>
</template>
